import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Radar, Loader2, Plus, Trash2, CheckCircle2, AlertOctagon,
  Globe, ExternalLink, RefreshCw, TrendingUp, Clock,
  Swords, Trophy, ChevronDown, ChevronUp, Target, Link2, History,
  TrendingDown, Minus,
} from "lucide-react";
import { AhrefsBreakdowns } from "@/components/AhrefsBreakdowns";

/**
 * Per-client AHREFS Feeds — assign either a workspace project OR a
 * free-form custom domain to a specific business unit. Live data
 * flows into overarching reports as soon as an assignment exists.
 *
 * Two-column layout:
 *   Left  — Assigned targets (per-BU) with delete + refresh actions
 *   Right — Add-new form (dropdown of workspace projects OR type-your-own
 *           domain with a Preview button that runs a paid overview call
 *           to confirm data returns before committing the assignment).
 */
export default function ClientAhrefsFeedsPage() {
  const { clientId } = useParams();
  const [bus, setBus] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [masterState, setMasterState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("project"); // "project" | "custom"
  const [selBu, setSelBu] = useState("");
  const [selProject, setSelProject] = useState("");
  const [customTarget, setCustomTarget] = useState("");
  const [customMode, setCustomMode] = useState("domain");
  const [customCountry, setCustomCountry] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [buRes, master] = await Promise.all([
        api.get(`/clients/${clientId}/business-units`),
        api.get("/ahrefs/master"),
      ]);
      setBus(buRes.data || []);
      setMasterState(master.data);
      if (master.data?.configured) {
        try {
          const [pj, asn] = await Promise.all([
            api.get("/ahrefs/projects"),
            api.get(`/clients/${clientId}/ahrefs/assignments`),
          ]);
          setProjects(pj.data.projects || []);
          setAssignments(asn.data.assignments || []);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to load AHREFS data");
        }
      }
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    if (!customTarget.trim()) {
      toast.error("Enter a domain first"); return;
    }
    setBusy("preview");
    setPreview(null);
    try {
      const { data } = await api.post("/ahrefs/preview", {
        target: customTarget.trim(),
        mode: customMode,
        country: customCountry.trim() || null,
      });
      setPreview(data);
      const dr = data?.totals?.domain_rating;
      if (data?.warnings?.length && !dr) {
        toast.error(`AHREFS: ${data.warnings[0]}`);
      } else {
        toast.success(dr ? `Preview OK — DR ${dr}` : "Preview returned data");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(null); }
  };

  const assign = async () => {
    if (!selBu) { toast.error("Pick a business unit"); return; }
    if (mode === "project" && !selProject) {
      toast.error("Pick a project"); return;
    }
    if (mode === "custom" && !customTarget.trim()) {
      toast.error("Enter a domain"); return;
    }
    setBusy("assign");
    try {
      await api.post(`/clients/${clientId}/ahrefs/assignments`,
        mode === "project"
          ? { business_unit_id: selBu, project_id: selProject }
          : { business_unit_id: selBu, target: customTarget.trim(),
              mode: customMode,
              country: customCountry.trim() || null });
      toast.success("Assigned");
      // Reset form
      setSelProject(""); setCustomTarget(""); setCustomCountry("");
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this AHREFS assignment? Live data for this property will stop appearing in reports.")) return;
    try {
      await api.delete(`/clients/${clientId}/ahrefs/assignments/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const batchRefresh = async () => {
    const staleCount = assignments.filter((a) => !a.last_snapshot).length;
    const label = staleCount
      ? `Refresh ${staleCount} unrefreshed properties`
      : "Force-refresh ALL properties";
    if (!window.confirm(
      `${label}? This runs sequentially and consumes AHREFS units — ` +
      `roughly 15s per property.`
    )) return;
    setBusy("batch");
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/refresh-all`,
        { stale_only: staleCount > 0 });
      const okCount = (data.refreshed || []).filter((r) => r.ok).length;
      const failCount = (data.refreshed || []).length - okCount;
      if (failCount) {
        toast.warning(
          `Batch: ${okCount} refreshed, ${failCount} failed, ` +
          `${data.skipped?.length || 0} skipped`);
      } else if (okCount) {
        toast.success(
          `Batch: ${okCount} properties refreshed` +
          (data.skipped?.length ? `, ${data.skipped.length} were fresh` : ""));
      } else {
        toast.success(`All ${data.total} properties are already fresh`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch refresh failed");
    } finally { setBusy(null); }
  };

  // Group assignments by BU for a cleaner list.
  const grouped = bus.map((b) => ({
    bu: b,
    rows: assignments.filter((a) => a.business_unit_id === b.business_unit_id),
  }));
  const unassigned = assignments.filter(
    (a) => !bus.some((b) => b.business_unit_id === a.business_unit_id));

  if (!masterState?.configured) {
    return (
      <div className="p-6" data-testid="ahrefs-feeds-page">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Radar className="h-12 w-12 text-[#F97316] mx-auto" />
          <h1 className="font-display text-3xl font-bold mt-4">AHREFS not configured</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            An agency admin needs to paste the AHREFS master API key on
            Settings → AHREFS Master before you can assign properties.
          </p>
          <a href="/settings/ahrefs-master"
            className="inline-flex items-center gap-1.5 mt-4 text-[#F97316]
              hover:underline text-sm font-semibold">
            Open AHREFS Master settings <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="ahrefs-feeds-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#F97316]/10 grid place-items-center">
            <Radar className="h-5 w-5 text-[#F97316]" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              AHREFS Data Sources
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Assign an AHREFS project OR a custom domain to each business
              unit. Assigned targets automatically flow into overarching
              reports under the AHREFS (SEO) service section.
            </p>
          </div>
        </div>
      </div>

      {/* ── HORIZONTAL "Add a target" STRIP ────────────────────────
          Was a sticky right-hand sidebar column. Now a collapsible
          full-width band pinned to the top, freeing the whole page
          width below for the assignment cards + comparison tables. */}
      <AddTargetStrip
        bus={bus} projects={projects}
        selBu={selBu} setSelBu={setSelBu}
        mode={mode} setMode={setMode}
        selProject={selProject} setSelProject={setSelProject}
        customTarget={customTarget} setCustomTarget={setCustomTarget}
        customMode={customMode} setCustomMode={setCustomMode}
        customCountry={customCountry} setCustomCountry={setCustomCountry}
        preview={preview} runPreview={runPreview}
        busy={busy} assign={assign} />

      {/* ── FULL-WIDTH ASSIGNMENTS AREA ────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="label-overline">Current assignments</div>
            {assignments.length > 0 && (() => {
              const stale = assignments.filter((a) => !a.last_snapshot).length;
              return (
                <div className="text-[11px] text-zinc-500">
                  {assignments.length} total · {stale} stale
                </div>
              );
            })()}
          </div>
          {assignments.length > 0 && (
            <Button size="sm" variant="outline" onClick={batchRefresh}
              disabled={busy === "batch"}
              className="h-7 rounded-sm text-[11px] border-[#F97316]/40 text-[#F97316] hover:bg-orange-50"
              data-testid="ahrefs-refresh-all">
              {busy === "batch"
                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                : <RefreshCw className="h-3 w-3 mr-1" />}
              Force-refresh all
            </Button>
          )}
        </div>

        {assignments.length === 0 ? (
          <Card className="rounded-sm border-dashed border-zinc-300 bg-zinc-50">
            <CardContent className="p-6 text-center text-sm text-zinc-500">
              <Radar className="h-6 w-6 mx-auto mb-2 text-zinc-400" />
              No AHREFS targets assigned yet. Use <strong>Add a target</strong>
              {" "}above to link an AHREFS project or a custom domain to a
              business unit.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {assignments.map((a) => (
              <AssignmentDashboard key={a.assignment_id}
                clientId={clientId} row={a}
                onDelete={remove}
                onRefreshed={load} />
            ))}
          </div>
        )}

        {unassigned.length > 0 && (
          <Card className="rounded-sm border-amber-200 bg-amber-50">
            <CardContent className="p-3">
              <div className="text-[11px] text-amber-800">
                <AlertOctagon className="h-3 w-3 inline mr-1" />
                {unassigned.length} assignment{unassigned.length === 1 ? "" : "s"} reference deleted business units — remove them below.
              </div>
              {unassigned.map((r) => (
                <div key={r.assignment_id} className="flex items-center gap-2 mt-2">
                  <div className="flex-1 text-[11px] font-mono">{r.target}</div>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.assignment_id)}
                    className="h-6 text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


/**
 * AddTargetStrip — horizontal collapsible band at the top of the
 * AHREFS Feeds page. Replaces the old 1/3-width sticky sidebar so
 * the assignments (and their wide comparison tables) can use the
 * full page width below. Defaults to collapsed; a single click on
 * the "+ Add a target" summary expands into a grid layout with
 * every input side-by-side.
 */
function AddTargetStrip({
  bus, projects, selBu, setSelBu, mode, setMode,
  selProject, setSelProject,
  customTarget, setCustomTarget,
  customMode, setCustomMode,
  customCountry, setCustomCountry,
  preview, runPreview, busy, assign,
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="rounded-sm border-zinc-200 overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2
          px-5 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors"
        data-testid="ahrefs-add-target-toggle">
        <div className="flex items-center gap-2 label-overline">
          <Plus className="h-3.5 w-3.5 text-[#F97316]" /> Add a target
        </div>
        <span className="text-[10px] font-mono text-zinc-500">
          {open ? "▲ collapse" : "▼ expand"}
        </span>
      </button>
      {open && (
        <CardContent className="p-5 space-y-4">
          {/* Row 1 — Business unit + Mode toggle span full width. */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-4">
              <Label className="label-overline">Business unit</Label>
              <Select value={selBu} onValueChange={setSelBu}>
                <SelectTrigger className="mt-1 rounded-sm text-[12px] h-9"
                  data-testid="ahrefs-bu-select">
                  <SelectValue placeholder="Pick a business unit" />
                </SelectTrigger>
                <SelectContent>
                  {bus.map((b) => (
                    <SelectItem key={b.business_unit_id}
                      value={b.business_unit_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-3">
              <Label className="label-overline">Source</Label>
              <div className="mt-1 flex gap-1 rounded-sm bg-zinc-100 p-1">
                <button type="button" onClick={() => setMode("project")}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-sm
                    ${mode === "project" ? "bg-white shadow-sm text-[#F97316]" : "text-zinc-500"}`}
                  data-testid="ahrefs-mode-project">
                  From project ({projects.length})
                </button>
                <button type="button" onClick={() => setMode("custom")}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded-sm
                    ${mode === "custom" ? "bg-white shadow-sm text-[#F97316]" : "text-zinc-500"}`}
                  data-testid="ahrefs-mode-custom">
                  Custom domain
                </button>
              </div>
            </div>

            {mode === "project" ? (
              <div className="md:col-span-5">
                <Label className="label-overline">AHREFS project</Label>
                <Select value={selProject} onValueChange={setSelProject}>
                  <SelectTrigger className="mt-1 rounded-sm text-[12px] h-9"
                    data-testid="ahrefs-project-select">
                    <SelectValue placeholder={
                      projects.length ? "Pick a project" : "No projects in workspace"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.project_id} value={String(p.project_id)}>
                        <div className="flex items-center gap-1.5">
                          <span>{p.project_name}</span>
                          {p.verified && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                          <span className="text-zinc-400 text-[10px] font-mono">
                            · {p.url}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="md:col-span-5">
                <Label className="label-overline">Domain / URL</Label>
                <div className="mt-1 relative">
                  <Globe className="h-3.5 w-3.5 text-zinc-400 absolute
                    left-2 top-1/2 -translate-y-1/2" />
                  <Input value={customTarget}
                    onChange={(e) => setCustomTarget(e.target.value)}
                    placeholder="e.g. gbegroup.com.au"
                    className="rounded-sm text-[12px] pl-7 h-9"
                    data-testid="ahrefs-custom-target" />
                </div>
              </div>
            )}
          </div>

          {mode === "project" ? (
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              Verified projects (green ✔) return the richest data.
              If the domain you want isn&apos;t here, switch to &ldquo;Custom domain&rdquo;.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-3">
                <Label className="label-overline">Mode</Label>
                <Select value={customMode} onValueChange={setCustomMode}>
                  <SelectTrigger className="mt-1 rounded-sm text-[12px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domain">domain</SelectItem>
                    <SelectItem value="subdomains">subdomains</SelectItem>
                    <SelectItem value="prefix">prefix</SelectItem>
                    <SelectItem value="exact">exact</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="label-overline">Country (opt)</Label>
                <Input value={customCountry}
                  onChange={(e) => setCustomCountry(e.target.value.toUpperCase())}
                  placeholder="AU / US / GB"
                  className="mt-1 rounded-sm text-[12px] h-9 uppercase font-mono"
                  maxLength={2} />
              </div>
              <div className="md:col-span-3">
                <Button size="sm" variant="outline" onClick={runPreview}
                  disabled={busy !== null || !customTarget.trim()}
                  className="rounded-sm w-full h-9"
                  data-testid="ahrefs-preview-btn">
                  {busy === "preview"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Radar className="h-3.5 w-3.5 mr-1.5" />}
                  Preview before assigning
                </Button>
              </div>
              {preview && (
                <div className="md:col-span-12 rounded-sm border border-zinc-200 bg-zinc-50
                  p-2.5 text-[11px]"
                  data-testid="ahrefs-preview-result">
                  <div className="font-display font-semibold text-[12px] mb-1">Preview</div>
                  {Object.entries(preview.totals || {}).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      {Object.entries(preview.totals).slice(0, 6).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-zinc-500 uppercase text-[9px] font-mono">
                            {k.replace(/_/g, " ")}
                          </span>
                          <div className="font-bold text-[#F97316]">
                            {typeof v === "number" ? v.toLocaleString() : String(v)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-red-700">
                      No data returned. Warnings: {preview.warnings?.join("; ") || "none"}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={assign} disabled={busy !== null}
              className="rounded-sm bg-[#F97316] hover:bg-[#EA580C] text-white
                px-6 h-9"
              data-testid="ahrefs-assign-btn">
              {busy === "assign"
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : "Assign to business unit"}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/**
 * AssignmentDashboard — one card per AHREFS assignment showing the
 * cached live snapshot (DR/traffic/keywords/backlinks totals + the
 * four breakdown tables from `AhrefsBreakdowns`). Refresh button
 * calls the paid `preview_ahrefs_all` on demand — the admin controls
 * exactly when a unit-consuming call fires. Never auto-runs on
 * mount, which would silently burn workspace units on every page load.
 */
function AssignmentDashboard({ clientId, row, onDelete, onRefreshed }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyCompete, setBusyCompete] = useState(false);
  const [showCompete, setShowCompete] = useState(false);
  const [competeOverride, setCompeteOverride] = useState(null);
  const [busyOverlap, setBusyOverlap] = useState(false);
  const [showOverlap, setShowOverlap] = useState(false);
  const [overlapOverride, setOverlapOverride] = useState(null);
  const [busySync, setBusySync] = useState(false);
  const [projOverride, setProjOverride] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const snap = row.last_snapshot || null;
  const compete = competeOverride || row.competitor_snapshot || null;
  const overlap = overlapOverride || row.keyword_overlap || null;
  const project = projOverride || row.ahrefs_project || null;
  const totals = snap?.totals || {};
  const hasData = !!snap && Object.keys(totals).length > 0;

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${row.assignment_id}/refresh`);
      onRefreshed?.(data.snapshot);
      setExpanded(true);
      const w = data.snapshot?.warnings || [];
      if (w.length) {
        toast.warning(`Refreshed with ${w.length} warning(s) — expand for details`);
      } else {
        toast.success("Snapshot refreshed from AHREFS");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setBusy(false); }
  };

  const fetchCompetitors = async () => {
    // Total competitors = 10 AHREFS-auto-suggested + admin-saved + AHREFS
    // Rank-Tracker project custom competitors (once synced). We surface
    // ALL of them so the panel matches what the admin sees inside AHREFS.
    const savedCount = (row.competitors || []).filter((c) => c.domain).length;
    const projCount = (project?.competitors || []).length;
    const totalComps = 10 + savedCount + projCount;
    if (!window.confirm(
      `This will consume ~${(1 + 5 + 5 * totalComps)} AHREFS units ` +
      `(1 competitors call + 5 position calls for your target + ` +
      `5 for each of ${totalComps} competitor${totalComps === 1 ? '' : 's'}` +
      `${projCount ? ` — including ${projCount} synced from your AHREFS project` : ''}` +
      `${savedCount ? ` and ${savedCount} you manually saved` : ''}). Continue?`
    )) return;
    setBusyCompete(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${row.assignment_id}/competitors`,
        { competitor_limit: 10 });
      setCompeteOverride(data.snapshot);
      setShowCompete(true);
      const w = data.snapshot?.warnings || [];
      toast.success(`Fetched ${data.snapshot?.competitors?.length || 0} competitors` +
        (w.length ? ` (${w.length} warnings)` : ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Competitor fetch failed");
    } finally { setBusyCompete(false); }
  };

  const syncAhrefsProject = async () => {
    setBusySync(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${row.assignment_id}/sync-project`);
      setProjOverride(data.ahrefs_project);
      const n = data.ahrefs_project?.competitors?.length || 0;
      toast.success(
        `Synced ${n} custom competitor${n === 1 ? '' : 's'} from AHREFS project ` +
        `"${data.ahrefs_project?.project_name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AHREFS project sync failed");
    } finally { setBusySync(false); }
  };

  // Count of usable competitors for the overlap panel — the union of
  // freshly-fetched auto-suggested, admin-saved and AHREFS-project-synced.
  // Overlap needs at least one.
  const compDomains = (compete?.competitors || [])
    .map((c) => c.domain).filter(Boolean);
  const savedComps = (row.competitors || [])
    .map((c) => c.domain).filter(Boolean);
  const projComps = project?.competitors || [];
  const overlapCompCount = compDomains.length
    || (savedComps.length + projComps.length);

  const fetchOverlap = async () => {
    if (overlapCompCount === 0) {
      toast.error("Fetch or save competitors first — overlap needs at least one competitor domain");
      return;
    }
    // Combine ALL known competitor domains for this overlap (fresh from
    // AHREFS auto-suggest + admin-saved + AHREFS project-synced) so the
    // side-by-side table matches what the admin is actively tracking.
    const merged = Array.from(new Set([
      ...compDomains,
      ...savedComps,
      ...projComps,
    ].map((d) => d.trim().toLowerCase()).filter(Boolean)));
    // Backend caps at 10 competitors for units-cost reasons — mirror that
    // cap in the estimate we show the user.
    const capped = Math.min(10, merged.length);
    const est = 1 + capped;
    if (!window.confirm(
      `This will consume ~${est * 5} AHREFS units (${est} organic-keyword ` +
      `calls @ ~5 units each${capped < merged.length
        ? `, top-${capped} of ${merged.length} tracked competitors`
        : ""}). Continue?`
    )) return;
    setBusyOverlap(true);
    try {
      const body = merged.length ? { competitor_domains: merged } : {};
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${row.assignment_id}/keyword-overlap`,
        body);
      setOverlapOverride(data.overlap);
      setShowOverlap(true);
      const n = data.overlap?.total_keywords || 0;
      toast.success(`Merged ${n.toLocaleString()} unique keywords across ${data.overlap?.domains?.length || 0} domains`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Keyword overlap fetch failed");
    } finally { setBusyOverlap(false); }
  };

  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden"
      data-testid={`ahrefs-assignment-${row.assignment_id}`}>
      <div className="flex items-center gap-2 px-2.5 py-2 bg-zinc-50 border-b border-zinc-100">
        <Badge className={`rounded-sm text-[9px] font-mono
          ${row.source === "project"
            ? "bg-emerald-100 text-emerald-800"
            : "bg-amber-100 text-amber-800"}`}>
          {row.source === "project" ? "PROJECT" : "CUSTOM"}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-[12px] truncate">
            {row.label || row.target}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">
            {row.target} · {row.mode}
            {row.country ? ` · ${row.country}` : ""}
          </div>
        </div>
        <Button size="sm" variant="outline"
          onClick={refresh} disabled={busy}
          className="h-7 rounded-sm text-[11px]"
          data-testid={`ahrefs-refresh-${row.assignment_id}`}>
          {busy
            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
            : <RefreshCw className="h-3 w-3 mr-1" />}
          {hasData ? "Refresh" : "Fetch live data"}
        </Button>
        <Button size="icon" variant="ghost" onClick={onDelete}
          className="h-7 w-7 text-red-500 hover:bg-red-50"
          data-testid={`ahrefs-remove-${row.assignment_id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {!hasData && (
        <div className="p-3 text-[11px] text-zinc-500 italic flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" />
          No live snapshot cached yet — click <strong>Fetch live data</strong> above
          to pull the SEO dashboard from AHREFS (consumes workspace units).
        </div>
      )}

      {hasData && (
        <div className="p-3 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              <Clock className="h-3 w-3" />
              Snapshot from {new Date(snap.captured_at).toLocaleString()}
              {snap.captured_by ? ` · by ${snap.captured_by}` : ""}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline"
                onClick={syncAhrefsProject} disabled={busySync}
                title={project
                  ? `Re-sync custom competitors from AHREFS project "${project.project_name}"`
                  : "Pull the custom competitors you've added inside AHREFS' own project UI"}
                className="h-6 rounded-sm text-[10px] border-sky-500/40 text-sky-700 hover:bg-sky-50"
                data-testid={`ahrefs-sync-project-${row.assignment_id}`}>
                {busySync
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <Link2 className="h-3 w-3 mr-1" />}
                {project
                  ? `Synced ${project.competitors?.length || 0} custom`
                  : "Sync AHREFS project"}
              </Button>
              <Button size="sm" variant="outline"
                onClick={fetchCompetitors} disabled={busyCompete}
                className="h-6 rounded-sm text-[10px] border-[#F97316]/40 text-[#F97316] hover:bg-orange-50"
                data-testid={`ahrefs-compete-${row.assignment_id}`}>
                {busyCompete
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <Swords className="h-3 w-3 mr-1" />}
                {compete ? "Re-run vs competitors" : "Compare vs competitors"}
              </Button>
              <Button size="sm" variant="outline"
                onClick={fetchOverlap}
                disabled={busyOverlap || overlapCompCount === 0}
                title={overlapCompCount === 0
                  ? "Compare vs competitors first"
                  : `Side-by-side keyword table vs ${overlapCompCount} competitor(s)`}
                className="h-6 rounded-sm text-[10px] border-emerald-500/40 text-emerald-700 hover:bg-emerald-50"
                data-testid={`ahrefs-overlap-${row.assignment_id}`}>
                {busyOverlap
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <Target className="h-3 w-3 mr-1" />}
                {overlap ? "Re-run keyword overlap" : "Keyword overlap"}
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => setShowHistory((v) => !v)}
                className="h-6 rounded-sm text-[10px] border-violet-500/40 text-violet-700 hover:bg-violet-50"
                title="Month-over-month deltas from persisted snapshots"
                data-testid={`ahrefs-history-${row.assignment_id}`}>
                <History className="h-3 w-3 mr-1" />
                {showHistory ? "Hide history" : "Show history"}
              </Button>
              <button type="button" onClick={() => setExpanded((v) => !v)}
                className="text-[10px] font-mono uppercase tracking-wider
                  text-zinc-500 hover:text-[#F97316]"
                data-testid={`ahrefs-toggle-${row.assignment_id}`}>
                {expanded ? "Hide details" : "Show details"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
            {[
              ["Domain Rating", totals.domain_rating, 1],
              ["Org. traffic", totals.org_traffic, 0],
              ["Org. keywords", totals.org_keywords, 0],
              ["Backlinks", totals.backlinks, 0],
              ["Ref. domains", totals.refdomains, 0],
              ["Org. traffic value", totals.org_traffic_value, 0, "$"],
            ].filter(([, v]) => v != null).map(([lbl, v, digits, prefix]) => (
              <div key={lbl}
                className="rounded-sm bg-emerald-50 border border-emerald-100 px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">{lbl}</div>
                <div className="font-display font-bold text-[13px] text-emerald-900">
                  {(prefix || "") + Number(v).toLocaleString(undefined,
                    { minimumFractionDigits: digits, maximumFractionDigits: digits })}
                </div>
              </div>
            ))}
          </div>

          {compete && (
            <CompetitorComparison snapshot={compete}
              show={showCompete} onToggle={() => setShowCompete((v) => !v)}
              clientId={clientId} assignmentId={row.assignment_id}
              testIdPrefix={`compete-${row.assignment_id}`} />
          )}

          {overlap && (
            <KeywordOverlapPanel overlap={overlap}
              show={showOverlap} onToggle={() => setShowOverlap((v) => !v)}
              clientId={clientId} assignmentId={row.assignment_id}
              testIdPrefix={`overlap-${row.assignment_id}`} />
          )}

          {showHistory && (
            <HistoryTrendPanel clientId={clientId}
              assignmentId={row.assignment_id}
              testIdPrefix={`history-${row.assignment_id}`} />
          )}

          {expanded && (
            <>
              {snap.warnings?.length ? (
                <div className="text-[10px] text-amber-800 bg-amber-50
                  border border-amber-200 rounded-sm p-2 space-y-0.5">
                  {snap.warnings.map((w, i) => (
                    <div key={`${w}-${i}`} className="font-mono">• {w}</div>
                  ))}
                </div>
              ) : null}
              <AhrefsBreakdowns breakdowns={snap.breakdowns}
                testIdPrefix={`ahrefs-live-${row.assignment_id}`} />
            </>
          )}
        </div>
      )}
    </div>
  );
}



/**
 * CompetitorComparison — renders the "how do we stack up on Page 1?"
 * table. Each row is one domain (target or competitor); each column
 * is a position bucket (Top 3 / Page 1 / Page 2 / etc.). The client's
 * own row is highlighted so a reader can eyeball the delta at a glance.
 *
 * Sort order is Total keywords desc so the biggest sites (usually
 * directories or category leaders) surface first — the client learns
 * "who's the giant we're chasing" instantly.
 */
function SortableTh({ label, k, align = "right",
                      sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      onClick={() => onSort(k)}
      title="Click to sort · click again to reverse · third click resets"
      className={`py-2 px-2 whitespace-nowrap cursor-pointer select-none
        ${align === "left" ? "text-left" : "text-right"}
        ${active ? "text-sky-700" : "hover:text-zinc-800"}`}
      data-testid={`sort-${k}`}>
      {label} <span className="text-[8px] ml-0.5">{arrow}</span>
    </th>
  );
}


function CompetitorComparison({ snapshot, show, onToggle, testIdPrefix,
                                clientId, assignmentId }) {
  const target = snapshot?.target || {};
  const competitors = snapshot?.competitors || [];
  const buckets = snapshot?.buckets || [];
  // Track which competitor's content-gap panel is open + cache the
  // fetched gap results client-side so the admin can toggle without
  // re-fetching (the results don't change between clicks in a session).
  const [openGap, setOpenGap] = useState(null);  // competitor domain
  const [gapCache, setGapCache] = useState({});
  const [gapBusy, setGapBusy] = useState(null);
  // Sort state — clicking any column header cycles asc → desc → default.
  // "default" preserves the requested ordering: target on top, then
  // manual/synced competitors sorted by Top-3 desc, then AHREFS auto
  // rows underneath (with a light-grey background so the two tiers are
  // visually distinct).
  const [sortKey, setSortKey] = useState("__default");
  const [sortDir, setSortDir] = useState("desc");

  const cycleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("desc");
      return;
    }
    // Same header clicked again — flip direction; third click resets.
    if (sortDir === "desc") { setSortDir("asc"); return; }
    setSortKey("__default"); setSortDir("desc");
  };

  const openContentGap = async (competitorDomain) => {
    if (openGap === competitorDomain) {
      // Toggle closed
      setOpenGap(null);
      return;
    }
    setOpenGap(competitorDomain);
    if (gapCache[competitorDomain]) return;   // already cached
    setGapBusy(competitorDomain);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${assignmentId}/content-gap`,
        { competitor_domain: competitorDomain, limit: 50 });
      setGapCache((c) => ({ ...c, [competitorDomain]: data.gap }));
      const n = data.gap?.gaps?.length || 0;
      const total = data.gap?.total_gap_count || 0;
      toast.success(`Fetched top ${n} of ${total} keyword gaps for ${competitorDomain}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Content-gap fetch failed");
      setOpenGap(null);
    } finally { setGapBusy(null); }
  };

  // Merge target + competitors. When no explicit sort is chosen, the
  // list is grouped as: (1) target row pinned to the top; (2) manual /
  // project-synced competitors sorted by Top-3 desc — because that's
  // "who's beating us for the money bucket right now"; (3) AHREFS
  // auto-suggested rows underneath, greyed out. Any explicit sort key
  // sorts the entire body (auto + manual) with the target still pinned.
  const targetRow = {
    isTarget: true, domain: target.domain,
    distribution: target.distribution || {},
    source: "target",
  };
  const isManual = (c) => c.source && c.source !== "ahrefs";
  const buildRow = (c) => ({
    isTarget: false, ...c,
    _isManual: isManual(c),
  });
  const compRows = competitors.map(buildRow);

  // Value extractor for the current sort column. Bucket keys map to
  // r.distribution[b.key]; specials (`common_keywords`, `overlap_pct`,
  // `total_kw`, `domain`) map to top-level or distribution.total.
  const valueFor = (r, key) => {
    if (!key || key === "__default") return null;
    if (key === "domain") return (r.domain || "").toLowerCase();
    if (key === "common_keywords") return r.common_keywords ?? -1;
    if (key === "overlap_pct") return r.overlap_pct ?? -1;
    if (key === "total_kw") return (r.distribution?.total) ?? -1;
    return (r.distribution?.[key]) ?? -1;
  };
  const compareRows = (a, b) => {
    const av = valueFor(a, sortKey);
    const bv = valueFor(b, sortKey);
    if (typeof av === "string") return sortDir === "asc"
      ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === "asc" ? (av - bv) : (bv - av);
  };

  let sortedComps;
  if (sortKey === "__default") {
    const manuals = compRows.filter((r) => r._isManual)
      .sort((a, b) => (b.distribution?.["1-3"] ?? -1)
                     - (a.distribution?.["1-3"] ?? -1));
    const autos = compRows.filter((r) => !r._isManual);
    sortedComps = [...manuals, ...autos];
  } else {
    sortedComps = [...compRows].sort(compareRows);
  }
  const rows = [targetRow, ...sortedComps];

  const fmt = (v) => v == null ? "—" : Number(v).toLocaleString();
  const pctFmt = (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`;

  return (
    <div className="rounded-sm border border-[#F97316]/30 bg-orange-50/40
      overflow-hidden" data-testid={`${testIdPrefix}-panel`}>
      <div className="px-3 py-2 border-b border-[#F97316]/20 bg-orange-50
        flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5 text-[#F97316]" />
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#7C2D12]">
              Page-1 comparison vs {competitors.length} competitors
            </div>
            <div className="text-[10px] text-zinc-500">
              Snapshot from {new Date(snapshot.captured_at).toLocaleString()}
              {snapshot.captured_by ? ` · by ${snapshot.captured_by}` : ""}
            </div>
          </div>
        </div>
        <button type="button" onClick={onToggle}
          className="text-[10px] font-mono uppercase tracking-wider
            text-zinc-500 hover:text-[#F97316] flex items-center gap-1"
          data-testid={`${testIdPrefix}-toggle`}>
          {show ? <>Hide <ChevronUp className="h-3 w-3" /></>
                : <>Show <ChevronDown className="h-3 w-3" /></>}
        </button>
      </div>

      {show && (
        <div className="p-3 space-y-3">
          {snapshot.warnings?.length ? (
            <div className="text-[10px] text-amber-800 bg-amber-50
              border border-amber-200 rounded-sm p-2 space-y-0.5">
              {snapshot.warnings.slice(0, 5).map((w, i) => (
                <div key={`${w}-${i}`} className="font-mono">• {w}</div>
              ))}
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="w-full text-[11px] bg-white
              border border-zinc-200 rounded-sm"
              data-testid={`${testIdPrefix}-table`}>
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50
                  text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                  <SortableTh label="Domain" k="domain" align="left"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} />
                  {buckets.map((b) => (
                    <SortableTh key={b.key} label={b.label} k={b.key}
                      sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} />
                  ))}
                  <SortableTh label="Total kw" k="total_kw"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} />
                  <SortableTh label="Common" k="common_keywords"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} />
                  <SortableTh label="Overlap" k="overlap_pct"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const d = r.distribution || {};
                  const canDrilldown = !r.isTarget && r.domain
                                       && clientId && assignmentId;
                  const isOpen = canDrilldown && openGap === r.domain;
                  const gap = gapCache[r.domain];
                  const isAuto = !r.isTarget && !r._isManual;
                  return (
                    <React.Fragment key={r.domain || `row-${i}`}>
                    <tr
                      className={`border-b border-zinc-100 last:border-b-0
                        ${r.isTarget
                          ? "bg-emerald-50 font-semibold"
                          : (isAuto ? "bg-zinc-100/70 hover:bg-zinc-200/60"
                                    : "hover:bg-zinc-50")}
                        ${canDrilldown ? "cursor-pointer" : ""}`}
                      onClick={canDrilldown ? () => openContentGap(r.domain) : undefined}
                      data-testid={r.isTarget
                        ? `${testIdPrefix}-target-row`
                        : `${testIdPrefix}-competitor-${i}`}>
                      <td className="py-1.5 px-2">
                        <div className="flex items-center gap-1.5">
                          {r.isTarget && (
                            <Badge className="rounded-sm text-[8px]
                              font-mono bg-emerald-600 text-white">
                              YOU
                            </Badge>
                          )}
                          {isAuto && (
                            <Badge className="rounded-sm text-[8px]
                              font-mono bg-zinc-400 text-white"
                              title="AHREFS auto-suggested competitor (not manually tracked)">
                              AUTO
                            </Badge>
                          )}
                          {!r.isTarget && r._isManual && (
                            <Badge className="rounded-sm text-[8px]
                              font-mono bg-sky-600 text-white"
                              title="Manually tracked competitor from AHREFS project / assignment settings">
                              TRACKED
                            </Badge>
                          )}
                          {canDrilldown && (
                            isOpen
                              ? <ChevronUp className="h-3 w-3 text-[#F97316]" />
                              : <ChevronDown className="h-3 w-3 text-zinc-400" />
                          )}
                          <span className={r.isTarget ? "text-emerald-900" : "text-zinc-700"}>
                            {r.domain}
                          </span>
                        </div>
                        {d.sample_capped && (
                          <div className="text-[9px] font-mono text-amber-700 mt-0.5">
                            top 1,000 kw sample
                          </div>
                        )}
                      </td>
                      {buckets.map((b) => (
                        <td key={b.key}
                          className={`py-1.5 px-2 text-right font-mono
                            ${r.isTarget ? "text-emerald-900" : ""}`}>
                          {fmt(d[b.key])}
                        </td>
                      ))}
                      <td className="py-1.5 px-2 text-right font-mono">
                        {fmt(d.total)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">
                        {r.isTarget ? "—" : fmt(r.common_keywords)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">
                        {r.isTarget ? "—" : pctFmt(r.overlap_pct)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-orange-50/60 border-b border-orange-200"
                        data-testid={`${testIdPrefix}-gap-panel-${i}`}>
                        <td colSpan={buckets.length + 4} className="p-3">
                          <ContentGapPanel
                            competitor={r.domain}
                            gap={gap}
                            busy={gapBusy === r.domain}
                            testIdPrefix={`${testIdPrefix}-gap-${i}`} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[10px] text-zinc-500 leading-relaxed">
            <strong>How to read this:</strong> each column shows how many
            keywords the domain ranks for in that Google SERP band.
            &quot;Top 3&quot; is the money band — clicks concentrate there.
            &quot;Common&quot; is the number of keywords shared with your
            site; &quot;Overlap&quot; is that count as a percentage of your
            total keyword footprint. <strong>Click any competitor row</strong>
            {" "}to see the exact keywords they rank Page 1 for that you
            don&apos;t — those are your content-gap opportunities. Rows
            with a light-grey background are AHREFS
            <Badge className="rounded-sm text-[8px] font-mono mx-1 bg-zinc-400 text-white align-middle">AUTO</Badge>
            suggestions; the un-shaded rows above are the competitors you
            are actively
            <Badge className="rounded-sm text-[8px] font-mono mx-1 bg-sky-600 text-white align-middle">TRACKED</Badge>
            {" "}via the AHREFS project sync. Click any column header to
            re-sort the entire list; click a third time to reset.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ContentGapPanel — inline "steal this competitor's keywords" table.
 * Renders inside the Competitor Comparison row when the admin clicks
 * a competitor. Shows the keywords the competitor ranks Page 1 for
 * that our target does NOT rank at all (position > 100 OR missing).
 *
 * Sorted by competitor traffic desc so the biggest-prize opportunities
 * surface first. Colour-coded position pills (Top 3 in emerald, 4-10
 * in amber) so a reader can eyeball difficulty in one scan.
 */
function ContentGapPanel({ competitor, gap, busy, testIdPrefix }) {
  if (busy) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-zinc-500 p-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Fetching keyword gaps for {competitor}… (2 AHREFS units)
      </div>
    );
  }
  if (!gap) return null;
  const gaps = gap.gaps || [];
  const totalGaps = gap.total_gap_count || 0;

  const posBadge = (p) => {
    if (p == null) return null;
    const isTop3 = p <= 3;
    return (
      <Badge className={`rounded-sm text-[9px] font-mono ${
        isTop3 ? "bg-emerald-600 text-white"
               : "bg-amber-500 text-white"}`}>
        #{p}
      </Badge>
    );
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center gap-1.5">
        <Target className="h-3.5 w-3.5 text-[#F97316]" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#7C2D12]">
          Content-gap keywords vs {competitor}
        </div>
        <Badge variant="outline" className="rounded-sm text-[9px] font-mono">
          {gaps.length} of {totalGaps} shown
        </Badge>
      </div>
      <div className="text-[10px] text-zinc-600 leading-relaxed">
        These are keywords <strong>{competitor}</strong> ranks Page 1 for
        (positions 1-10) that your site does not rank for in its top
        {" "}{gap.target_sample_size} keywords. Sorted by their estimated
        organic traffic desc — biggest opportunities first.
      </div>
      {gaps.length === 0 ? (
        <div className="text-[11px] text-zinc-500 italic p-3
          bg-white border border-zinc-200 rounded-sm">
          No content gaps found — either your target ranks for the same
          set as this competitor, or the competitor&apos;s Page-1 sample
          didn&apos;t return any keywords.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200 rounded-sm">
          <table className="w-full text-[11px]"
            data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50
                text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                <th className="text-left py-2 px-2">Keyword</th>
                <th className="text-center py-2 px-2 w-14">Comp. pos</th>
                <th className="text-right py-2 px-2">Volume</th>
                <th className="text-right py-2 px-2">Comp. traffic</th>
                <th className="text-right py-2 px-2">CPC</th>
                <th className="text-right py-2 px-2">Difficulty</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((g, i) => (
                <tr key={g.keyword || `gap-${i}`} className="border-b border-zinc-100 last:border-b-0
                  hover:bg-orange-50/50"
                  data-testid={`${testIdPrefix}-row-${i}`}>
                  <td className="py-1.5 px-2 font-mono text-[11px]">
                    {g.keyword}
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    {posBadge(g.competitor_position)}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {g.volume == null ? "—" : Number(g.volume).toLocaleString()}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {g.traffic == null ? "—" : Number(g.traffic).toLocaleString()}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {g.cpc == null ? "—" : `$${Number(g.cpc).toFixed(2)}`}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {g.difficulty == null ? "—" : Math.round(g.difficulty)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SortableThOverlap({ label, k, align = "right", sticky, bordered,
                            colorClass, hint, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
  const cls = [
    "py-2 px-2 whitespace-nowrap cursor-pointer select-none",
    align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right",
    sticky ? "sticky left-0 bg-zinc-50 min-w-[240px] z-10" : "",
    bordered ? "border-l border-zinc-200" : "",
    active ? "text-sky-700" : "hover:text-zinc-800",
    colorClass || "",
  ].filter(Boolean).join(" ");
  const title = hint
    ? `${hint}\n\nClick to sort · click again to reverse`
    : "Click to sort · click again to reverse";
  return (
    <th onClick={() => onSort(k)}
      title={title}
      className={cls}
      data-testid={`overlap-sort-${k}`}>
      {label} <span className="text-[8px] ml-0.5">{arrow}</span>
    </th>
  );
}


/**
 * KeywordOverlapPanel — side-by-side "Keywords by domain" table modeled
 * after AHREFS' own overlap view. Every row is one keyword; every
 * domain contributes a column showing that domain's rank position and
 * organic traffic for the keyword. Blank cells = domain doesn't rank
 * in its top ~500 keywords sample.
 *
 * Filters:
 *   • Search box — substring match on the keyword text.
 *   • "Missing on target only" — hides keywords the client already
 *     ranks for, so the panel effectively becomes a multi-competitor
 *     content-gap view.
 *   • Row cap — default 200 with a "Show all" toggle. The full pivot
 *     can be 3-4k rows across 5 competitors; rendering all at once
 *     tanks scroll perf, so we chunk.
 */
function KeywordOverlapPanel({ overlap, show, onToggle, testIdPrefix,
                              clientId, assignmentId }) {
  const target = overlap?.target;
  const domains = overlap?.domains || [];
  const allKeywords = React.useMemo(() => overlap?.keywords || [], [overlap]);
  const [q, setQ] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Sort state — every column is clickable (keyword text, volume, KD,
  // CPC and every per-domain position column). Default sort is by
  // target traffic desc as returned by the backend.
  const [sortKey, setSortKey] = useState("__default");
  const [sortDir, setSortDir] = useState("desc");

  const cycleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir(key === "keyword" ? "asc" : "desc");
      return;
    }
    if (sortDir === "desc") { setSortDir("asc"); return; }
    if (sortDir === "asc")  { setSortDir("desc"); return; }
  };

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allKeywords.filter((row) => {
      if (needle && !row.keyword.toLowerCase().includes(needle)) return false;
      if (missingOnly) {
        const t = (row.domains || {})[target];
        if (t && t.position != null) return false;
      }
      return true;
    });
  }, [allKeywords, q, missingOnly, target]);

  // Extract the value used for sorting a given row + column key. Per-
  // domain columns use `dom:<domain>` as the key; NULLs sort to the
  // bottom on desc and top on asc.
  const valueFor = (row, key) => {
    if (key === "keyword") return (row.keyword || "").toLowerCase();
    if (key === "volume") return row.volume ?? -1;
    if (key === "difficulty") return row.difficulty ?? -1;
    if (key === "cpc") return row.cpc ?? -1;
    if (key.startsWith("dom:")) {
      const dom = key.slice(4);
      const cell = (row.domains || {})[dom] || {};
      // Ranking position — lower is better, so we invert for desc.
      // Missing ranks sort as position=101 so they land at the bottom
      // when sorting asc (best first).
      return cell.position ?? 101;
    }
    return 0;
  };

  const sorted = React.useMemo(() => {
    if (sortKey === "__default") return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = valueFor(a, sortKey);
      const bv = valueFor(b, sortKey);
      if (typeof av === "string") return sortDir === "asc"
        ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const rows = showAll ? sorted : sorted.slice(0, 200);
  const fmt = (v) => v == null ? "—" : Number(v).toLocaleString();

  const posPill = (pos) => {
    if (pos == null) {
      return <span className="text-zinc-300">—</span>;
    }
    const cls = pos <= 3
      ? "bg-emerald-600 text-white"
      : pos <= 10
        ? "bg-amber-500 text-white"
        : "bg-zinc-200 text-zinc-700";
    return (
      <Badge className={`rounded-sm text-[9px] font-mono ${cls}`}>
        #{pos}
      </Badge>
    );
  };

  const cellFor = (row, dom) => {
    const cell = (row.domains || {})[dom] || {};
    const isMe = dom === target;
    const who = isMe ? "You" : dom;
    const posTip = cell.position == null
      ? `${who} — does NOT rank in the top 500 for "${row.keyword}"`
      : `${who} — ranks #${cell.position} on Google for "${row.keyword}"${
          cell.position <= 3 ? " · Top 3 · money band" :
          cell.position <= 10 ? " · Page 1" :
          cell.position <= 20 ? " · Page 2" : " · Page 3+"
        }`;
    const trafTip = cell.traffic
      ? `Estimated organic traffic ${isMe ? "you get" : `${dom} gets`} from this keyword: ~${
          Math.round(cell.traffic).toLocaleString()} visits/month`
      : "";
    return (
      <td key={dom} className="py-1.5 px-2 text-center whitespace-nowrap
        border-l border-zinc-100">
        <div className="flex flex-col items-center gap-0.5">
          <span title={posTip}>{posPill(cell.position)}</span>
          {cell.traffic ? (
            <span className="text-[9px] font-mono text-zinc-500"
              title={trafTip}>
              {fmt(Math.round(cell.traffic))} tf
            </span>
          ) : null}
        </div>
      </td>
    );
  };

  return (
    <div className="rounded-sm border border-emerald-500/30 bg-emerald-50/40
      overflow-hidden" data-testid={`${testIdPrefix}-panel`}>
      <div className="px-3 py-2 border-b border-emerald-500/20 bg-emerald-50
        flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Target className="h-3.5 w-3.5 text-emerald-700" />
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-900">
              Keyword overlap · {allKeywords.length.toLocaleString()} keywords
              across {domains.length} domains
            </div>
            <div className="text-[10px] text-zinc-500">
              Snapshot from {new Date(overlap.captured_at).toLocaleString()}
              {overlap.captured_by ? ` · by ${overlap.captured_by}` : ""}
            </div>
          </div>
        </div>
        <button type="button" onClick={onToggle}
          className="text-[10px] font-mono uppercase tracking-wider
            text-zinc-500 hover:text-emerald-700 flex items-center gap-1"
          data-testid={`${testIdPrefix}-toggle`}>
          {show ? <>Hide <ChevronUp className="h-3 w-3" /></>
                : <>Show <ChevronDown className="h-3 w-3" /></>}
        </button>
      </div>

      {show && (
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input placeholder="Search keywords…" value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-7 rounded-sm text-[11px] w-56"
              data-testid={`${testIdPrefix}-search`} />
            <label className="flex items-center gap-1.5 text-[10px] text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={missingOnly}
                onChange={(e) => setMissingOnly(e.target.checked)}
                data-testid={`${testIdPrefix}-missing-only`} />
              Only keywords <strong>{target}</strong> is missing
            </label>
            {clientId && assignmentId && (
              <a href={`/clients/${clientId}/ahrefs-feeds/${assignmentId}/keyword-overlap`}
                target="_blank" rel="noopener noreferrer"
                className="text-[10px] font-mono uppercase tracking-wider
                  text-emerald-700 hover:text-emerald-900 border border-emerald-500/40
                  rounded-sm px-2 py-1 hover:bg-emerald-50 flex items-center gap-1"
                data-testid={`${testIdPrefix}-open-full`}>
                <ExternalLink className="h-3 w-3" /> Open in full window
              </a>
            )}
            <div className="ml-auto text-[10px] font-mono text-zinc-500">
              {rows.length.toLocaleString()} of {sorted.length.toLocaleString()} shown
            </div>
          </div>

          <div className="overflow-x-auto max-h-[560px] overflow-y-auto
            bg-white border border-zinc-200 rounded-sm">
            <table className="w-full text-[11px]"
              data-testid={`${testIdPrefix}-table`}>
              <thead className="sticky top-0 bg-zinc-50 z-10">
                <tr className="border-b border-zinc-200
                  text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                  <SortableThOverlap label="Keyword" k="keyword" align="left"
                    sticky sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}
                    hint="The search query users type into Google. Sort A→Z alphabetically." />
                  <SortableThOverlap label="Vol" k="volume"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}
                    hint="Monthly search volume — estimated Google searches per month." />
                  <SortableThOverlap label="KD" k="difficulty"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}
                    hint="Keyword Difficulty (0-100) — how hard it is to rank in Google's Top 10. 0-10 easy, 11-30 medium, 31-70 hard, 71-100 super hard." />
                  <SortableThOverlap label="CPC" k="cpc"
                    sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}
                    hint="Cost-per-click (USD) — average Google Ads paid per click. Signal of commercial intent." />
                  {domains.map((d) => (
                    <SortableThOverlap key={d} k={`dom:${d}`}
                      align="center" bordered
                      sortKey={sortKey} sortDir={sortDir} onSort={cycleSort}
                      hint={d === target
                        ? `Your site (${d}) — position pill shows YOUR Google rank; "tf" is estimated organic traffic/month.`
                        : `Competitor ${d} — position pill shows THEIR rank; "tf" is estimated organic traffic/month. Empty (—) means they don't rank in their top 500 keywords.`}
                      label={<>
                        {d === target && (
                          <Badge className="rounded-sm text-[8px] font-mono
                            bg-emerald-600 text-white mr-1">YOU</Badge>
                        )}
                        {d}
                      </>}
                      colorClass={d === target ? "text-emerald-900" : "text-zinc-600"} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4 + domains.length}
                      className="text-center italic text-zinc-500 py-6 text-[11px]">
                      No keywords match the current filters.
                    </td>
                  </tr>
                ) : rows.map((row, i) => (
                  <tr key={row.keyword || `kw-${i}`} className="border-b border-zinc-100 last:border-b-0
                    hover:bg-emerald-50/50"
                    data-testid={`${testIdPrefix}-row-${i}`}>
                    <td className="py-1.5 px-2 font-mono sticky left-0 bg-white
                      text-[11px] border-r border-zinc-100"
                      title={`Search query: "${row.keyword}"`}>
                      {row.keyword}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono"
                      title={row.volume == null ? "Monthly search volume unavailable"
                        : `Monthly search volume: ${Number(row.volume).toLocaleString()} searches/month`}>
                      {fmt(row.volume)}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono"
                      title={row.difficulty == null ? "Keyword difficulty unavailable"
                        : `Keyword Difficulty: ${Math.round(row.difficulty)}/100 — ${
                            row.difficulty <= 10 ? "Easy" :
                            row.difficulty <= 30 ? "Medium" :
                            row.difficulty <= 70 ? "Hard" : "Super Hard"
                          }`}>
                      {row.difficulty == null ? "—" : Math.round(row.difficulty)}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono"
                      title={row.cpc == null ? "Cost-per-click unavailable"
                        : `Google Ads cost-per-click: $${Number(row.cpc).toFixed(2)}`}>
                      {row.cpc == null ? "—" : `$${Number(row.cpc).toFixed(2)}`}
                    </td>
                    {domains.map((d) => cellFor(row, d))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sorted.length > 200 && (
            <div className="text-center">
              <button type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-[10px] font-mono uppercase tracking-wider
                  text-emerald-700 hover:text-emerald-900 border border-emerald-500/40
                  rounded-sm px-3 py-1 hover:bg-emerald-50"
                data-testid={`${testIdPrefix}-show-all`}>
                {showAll ? "Collapse to top 200"
                         : `Show all ${sorted.length.toLocaleString()} rows`}
              </button>
            </div>
          )}

          <div className="text-[10px] text-zinc-500 leading-relaxed">
            <strong>How to read this:</strong> each row is one keyword.
            Position pills show where each domain ranks — emerald = Top 3,
            amber = Page 1 (4-10), grey = elsewhere. &quot;tf&quot; below the
            pill is that domain&apos;s estimated organic traffic for the
            keyword. Toggle <em>Only keywords {target} is missing</em> to
            find high-value gaps your competitors dominate. Click any column
            header to sort — including per-domain columns to see who&apos;s
            winning that domain most keywords. Use <strong>Open in full
            window</strong> for a dedicated page with max screen real estate.
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * HistoryTrendPanel — pulls the persisted (client, assignment,
 * competitor, month) history rows and renders a compact table with
 * month-over-month deltas for the target's own metrics plus each
 * tracked competitor's content-gap keyword count and overlap %.
 * Loads lazily when the user toggles it open.
 */
function HistoryTrendPanel({ clientId, assignmentId, testIdPrefix }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/clients/${clientId}/ahrefs/assignments/${assignmentId}/history?months=12`);
        if (!alive) return;
        setRows(data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Failed to load history");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [clientId, assignmentId]);

  if (loading) {
    return (
      <div className="mt-2 rounded-sm border border-violet-200 bg-violet-50/60
        px-3 py-4 flex items-center gap-2 text-[11px] text-violet-800"
        data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Loading history…
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-2 rounded-sm border border-rose-200 bg-rose-50/60
        px-3 py-3 text-[11px] text-rose-800"
        data-testid={`${testIdPrefix}-error`}>{error}</div>
    );
  }
  const series = rows?.series || [];
  if (!series.length) {
    return (
      <div className="mt-2 rounded-sm border border-violet-200 bg-violet-50/60
        px-3 py-3 text-[11px] text-violet-800 leading-relaxed"
        data-testid={`${testIdPrefix}-empty`}>
        No historical snapshots yet. Every time you click <strong>Refresh</strong>,
        <strong> Compare vs competitors</strong>, or <strong>Content gap</strong>,
        a monthly row is persisted per (target, competitor, month). Come back
        next month to see month-over-month deltas.
      </div>
    );
  }
  const target = series.find((s) => s.role === "target");
  const competitors = series.filter((s) => s.role === "competitor");

  const _fmtDelta = (d) => {
    if (!d) return null;
    const arrow = d.delta > 0 ? <TrendingUp className="h-3 w-3" />
                : d.delta < 0 ? <TrendingDown className="h-3 w-3" />
                : <Minus className="h-3 w-3" />;
    const color = d.delta > 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                : d.delta < 0 ? "text-rose-700 bg-rose-50 border-rose-200"
                : "text-zinc-600 bg-zinc-50 border-zinc-200";
    const sign = d.delta > 0 ? "+" : "";
    const pct = d.delta_pct != null ? ` (${sign}${d.delta_pct}%)` : "";
    const val = Number(d.delta).toLocaleString(undefined,
      { maximumFractionDigits: 2 });
    return (
      <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5
        text-[10px] font-mono ${color}`}>
        {arrow}{sign}{val}{pct}
      </span>
    );
  };
  const _fmtCur = (v) => v == null ? "—"
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const targetMetricRows = [
    ["Domain Rating", "domain_rating"],
    ["Backlinks", "backlinks"],
    ["Referring domains", "refdomains"],
    ["Organic traffic", "org_traffic"],
    ["Organic keywords", "org_keywords"],
    ["Org. traffic value", "org_traffic_value"],
  ];

  return (
    <div className="mt-2 rounded-sm border border-violet-200 bg-violet-50/40
      px-3 py-3 space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-wider text-violet-900">
          Historical trend · last {rows.months_window} months
        </div>
        <div className="text-[10px] font-mono text-violet-700">
          {rows.row_count} row{rows.row_count === 1 ? "" : "s"} persisted
        </div>
      </div>

      {target && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider
            text-violet-800 mb-1">Target · {target.competitor_domain}</div>
          <div className="overflow-x-auto">
            <table className="text-[11px] w-full">
              <thead className="text-[9px] font-mono uppercase tracking-wider
                text-zinc-500">
                <tr>
                  <th className="text-left px-2 py-1">Metric</th>
                  <th className="text-right px-2 py-1">Current</th>
                  <th className="text-right px-2 py-1">Previous</th>
                  <th className="text-right px-2 py-1">Δ MoM</th>
                </tr>
              </thead>
              <tbody>
                {targetMetricRows.map(([lbl, key]) => {
                  const d = target.deltas?.[key];
                  return (
                    <tr key={key} className="border-t border-violet-100">
                      <td className="px-2 py-1 text-zinc-700">{lbl}</td>
                      <td className="px-2 py-1 text-right font-mono">
                        {_fmtCur(d?.current)}</td>
                      <td className="px-2 py-1 text-right font-mono text-zinc-500">
                        {_fmtCur(d?.previous)}</td>
                      <td className="px-2 py-1 text-right">
                        {_fmtDelta(d)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {competitors.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider
            text-violet-800 mb-1">
            Competitors ({competitors.length}) · gap keywords + overlap
          </div>
          <div className="overflow-x-auto">
            <table className="text-[11px] w-full">
              <thead className="text-[9px] font-mono uppercase tracking-wider
                text-zinc-500">
                <tr>
                  <th className="text-left px-2 py-1">Competitor</th>
                  <th className="text-right px-2 py-1">Overlap %</th>
                  <th className="text-right px-2 py-1">Gap keywords</th>
                  <th className="text-right px-2 py-1">Δ Gap MoM</th>
                  <th className="text-right px-2 py-1">Snapshots</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c) => {
                  const latest = c.months[0] || {};
                  const ov = latest.overlap || {};
                  const cg = latest.content_gap || {};
                  const dGap = c.deltas?.gap_keyword_count;
                  return (
                    <tr key={c.competitor_domain}
                      className="border-t border-violet-100">
                      <td className="px-2 py-1 text-zinc-700 font-mono">
                        {c.competitor_domain}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {ov.overlap_pct == null ? "—" : `${ov.overlap_pct}%`}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {cg.total_gap_count == null ? "—"
                          : cg.total_gap_count.toLocaleString()}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {dGap ? _fmtDelta(dGap) : (
                          <span className="text-[10px] text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-zinc-500">
                        {c.months.length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[10px] text-zinc-500 leading-relaxed">
        <strong>How this works:</strong> every AHREFS refresh persists a row
        keyed on (target, competitor, month) into <code>ahrefs_history</code>.
        Δ&nbsp;MoM compares the latest month with the previous one. Force a
        fresh snapshot by clicking <strong>Refresh</strong> or <strong>Compare
        vs competitors</strong> above.
      </div>
    </div>
  );
}
